import { makeAutoObservable, runInAction } from 'mobx'
import {
  InformationPagePayload,
  InformationTypes,
} from '~/shared/api/services/information/types'
import { InformationServices } from '~/shared/api/services/information'
import { handleAxiosError } from '~/shared/lib'
import {
  FormDataInformationPage,
  LoadersInformation,
  LocalizationItem,
} from './types'
import { SelectOption } from '~/shared/ui'
import { defaultStoreValues } from './lib-create-information/constants'

export class InformationStore {
  information: InformationTypes[] = []

  loaders: LoadersInformation = {
    information: false,
    loadMoreInformation: false,
    createInformationPage: false,
    updateInformationPage: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  formData: FormDataInformationPage = {
    storeId: '',
    slug: '',
    localizations: [defaultStoreValues],
  }

  informationById: FormDataInformationPage = {
    storeId: '',
    slug: '',
    localizations: [defaultStoreValues],
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof LoadersInformation) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof LoadersInformation) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormData = <K extends keyof FormDataInformationPage>(
    key: K,
    value: FormDataInformationPage[K],
  ) => {
    this.formData[key] = value
  }

  setDefaultFormData = (defaultValues: LocalizationItem[]) => {
    this.formData.localizations = defaultValues
  }

  getInformationById = (infoById: FormDataInformationPage) => {
    this.informationById = infoById
  }

  getInformationPage = async (action?: 'loadMoreInformation') => {
    if (action === 'loadMoreInformation' && this.requestSettings.isAllLoaded)
      return
    this.activeLoader(action || 'information')

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset:
          action !== 'loadMoreInformation' ? 0 : this.requestSettings.offset,
      }

      const { data } = await InformationServices.getInformationPage({ params })

      if (action === 'loadMoreInformation') {
        runInAction(() => {
          this.information = [...this.information, ...data]
        })
      } else {
        runInAction(() => {
          this.information = data
        })
      }
      this.requestSettings = {
        ...this.requestSettings,
        isAllLoaded: data.length < this.requestSettings.limit,
        offset: action
          ? this.requestSettings.offset + this.requestSettings.limit
          : 20,
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'information')
    }
  }

  createInformationPage = async () => {
    try {
      this.activeLoader('createInformationPage')
      const storeId = this.formData.storeId as SelectOption | string
      const selectedStoreId =
        typeof storeId === 'string' ? storeId : storeId?.value

      await InformationServices.createInformationPage({
        storeId: selectedStoreId || null,
        slug: this.formData.slug || null,
        localizations: this.formData.localizations.map((localization) => ({
          ...localization,
          title: localization.title || null,
          content: localization.content || null,
          seoTitle: localization.seoTitle || null,
          seoKeywords: localization.seoKeywords || null,
          seoDescription: localization.seoDescription || null,
        })),
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createInformationPage')
    }
  }

  updateInformationPage = async (id: string, data: FormDataInformationPage) => {
    try {
      const storeId =
        typeof data.storeId === 'string' ? data.storeId : data.storeId?.value
      const dataToSend: InformationPagePayload = {
        ...data,
        id: data.id || null,
        storeId: storeId || null,
        slug: data.slug || null,
        localizations: data.localizations.map((localization) => ({
          ...localization,
          title: localization.title || null,
          content: localization.content || null,
          seoTitle: localization.seoTitle || null,
          seoKeywords: localization.seoKeywords || null,
          seoDescription: localization.seoDescription || null,
        })),
      }

      if (this.informationById.slug === data.slug) {
        delete dataToSend.slug
      }

      this.activeLoader('updateInformationPage')
      await InformationServices.updateInformationPage(id, dataToSend)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('updateInformationPage')
    }
  }

  removeInformationPage = async (id: string) => {
    try {
      await InformationServices.removeInformationPage(id)
      this.information = this.information.filter((item) => item.id !== id)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  clearFormData = () => {
    this.formData = {
      storeId: '',
      slug: '',
      localizations: [defaultStoreValues],
    }
  }

  setLocalizationField = (
    tabIndex: number,
    field: keyof LocalizationItem,
    value: string,
  ) => {
    runInAction(() => {
      const updatedLocalizations = [...this.formData.localizations]

      updatedLocalizations[tabIndex] = updatedLocalizations[tabIndex]
        ? {
            ...updatedLocalizations[tabIndex],
            [field]: value,
          }
        : {
            ...defaultStoreValues,
            [field]: value,
          }

      this.formData.localizations = updatedLocalizations
    })
  }
}
